import React, { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Route, BrowserRouter, Switch, Redirect } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/ja'; // locale-data for ja
import '@formatjs/intl-relativetimeformat/polyfill';
import '@formatjs/intl-relativetimeformat/locale-data/ja'; // locale-data for ja
import messages from '../lib/i18n';
import { checkCompatible, isCompanyUser } from '../lib/tools';
import { getUserInfo, getUserCompanies } from '../actions/api/apis';
import getGroups from '../actions/api/group/getGroups';
import config from '../config';
import { PAGE_PATH, ROLE, PROVIDER, COOKIE_KEYS } from '../lib/constants';
import confirmDeviceAuth from '../actions/api/deviceAuth/confirmDeviceAuth';
import flattenMessages from '../functions/flattenMessages';

import NotFound from './NotFound';
import ScreenLock from './ScreenLock';
import NotRole from './NotRole';
import LoginFailure from './Auth/LoginFailure';
import Loading from './Loading';
import TimeLineContainer from '../containers/TimeLineContainer';
import TimeLineTileContainer from '../containers/TimeLineTileContainer';
import TrashContainer from '../containers/TrashContainer';
import TrashMediaContainer from '../containers/TrashMediaContainer';
import MainContainer from '../containers/MainContainer';
import MediaContainer from '../containers/MediaContainer';
import V2LoginContainer from '../containers/V2LoginContainer';
import V2LogoutContainer from '../containers/V2LogoutContainer';
import IssueContainer from '../containers/IssueContainer';
import StaffContainer from '../containers/StaffContainer';
import CompanyContainer from '../containers/CompanyContainer';
import MapContainer from '../containers/MapContainer';
import DeviceContainer from '../containers/DeviceContainer';
import MasterContainer from '../containers/MasterContainer';
import RemoteContainer from '../containers/RemoteContainer';
import ActivationContainer from '../containers/ActivationContainer';
import GroupContainer from '../containers/GroupContainer';
import RemotePreparationContainer from '../containers/RemotePreparationContainer';
import ActionLogContainer from '../containers/ActionLogContainer';
import '../styles/main.css';
import ReportContainer from '../containers/ReportContainer';
import ReportDetailContainer from '../containers/ReportDetailContainer';
import DeviceAuthPage from './DeviceAuth/DeviceAuthPage';
import ReportEditContainer from '../containers/ReportEditContainer';
import AccessControlContainer from '../containers/AccessControlContainer';

export const ExApp = ({
  apiToken,
  dispatch,
  isMobile,
  locale,
  innerRole,
  operatorId,
  acquiredMaster,
}) => {
  // TODO: コンポーネントのネストはパフォーマンスに影響するため、修正が必要
  /* eslint react/no-unstable-nested-components: off */
  const ExRoute = ({ component: Component, isRemote, ...rest }) => {
    // TODO: デバイス認証周りの処理をカスタムフックに切り出す
    const requiredDeviceAuth = useSelector(
      state => state.deviceAuth.requiredDeviceAuth,
    );
    let deviceAuthToken = useSelector(
      state => state.deviceAuth.deviceAuthToken,
    );
    const [cookies] = useCookies([COOKIE_KEYS.DEVICE_AUTH_TOKEN]);

    if (!deviceAuthToken && cookies.deviceAuthToken) {
      deviceAuthToken = cookies.deviceAuthToken;
    }
    const isLogin = useSelector(state => state.main.isLogin);

    /**
     * アクティベーション画面でデバイス認証を行うとリソースがなくエラーが発生するため、デバイス認証を行わない
     */
    useEffect(() => {
      if (isLogin && !rest.path.includes('activation')) {
        dispatch(confirmDeviceAuth(apiToken, deviceAuthToken));
      }
    }, [deviceAuthToken, isLogin, rest.path]);

    // デバイス認証が必要な場合はデバイス認証画面にリダイレクトする
    if (requiredDeviceAuth === true && rest.path !== '/device-auth') {
      return <Redirect to="/device-auth" />;
    }

    const compatibleInfo = checkCompatible();
    dispatch({ type: 'UPDATE_COMPATIBLE_INFO', compatibleInfo });
    if (compatibleInfo && !compatibleInfo.isSupportedDeviceVersion) {
      const i18nMessages = messages[locale];
      const msgs = JSON.parse(i18nMessages['screenLock.messages.notSupported']);
      return <Route {...rest} render={() => <ScreenLock msgs={msgs} />} />;
    }
    // かん助 判定
    const isCansuke = rest.path.match(`/${PROVIDER.cansuke}`);
    if (isCansuke) {
      dispatch({ type: 'UPDATE_PROVIDER', providerName: PROVIDER.cansuke });
    }
    // ログインされていなかった場合はLogin処理を行う
    if (!apiToken) {
      if (isCansuke) {
        return (
          <Route
            {...rest}
            render={() => <V2LoginContainer provider={PROVIDER.cansuke} />}
          />
        );
      }
      return <Route {...rest} render={() => <V2LoginContainer />} />;
    }
    if (
      isCompanyUser(innerRole) &&
      !isRemote &&
      !(rest.path === '/activation/' || rest.path === '/ds/activation/')
    ) {
      // 企業所属参加ユーザの場合はユーザ企業、ユーザグループを取得する
      dispatch(getUserCompanies(apiToken));
      // グループ情報
      dispatch(getGroups(apiToken, { isUsable: true }));
      // マスタデータ未取得の場合は初回表示時に取得する
      if (!acquiredMaster) {
        dispatch({ type: 'UPDATE_ACQUIRED_MASTER', value: true });
      }
    }
    // ロールによって表示出来るページを制御する
    const auth =
      rest.roles === undefined ||
      (rest.roles && rest.roles.indexOf(innerRole) !== -1);
    switch (innerRole) {
      case ROLE.INITIAL: {
        if (apiToken && apiToken !== '') {
          if (rest.path === '/activation/' || rest.path === '/ds/activation/') {
            const renderComponent = props => <Component {...props} />;
            return <Route {...rest} render={props => renderComponent(props)} />;
          }
          dispatch(getUserInfo(apiToken));
          const loadingMessages = ['Loading...'];
          return (
            <Route
              {...rest}
              render={() => <Loading loadingMessages={loadingMessages} />}
            />
          );
        }
        return <Route {...rest} render={() => <NotFound />} />;
      }
      case ROLE.GENERAL: {
        const renderComponent = props =>
          (auth && <Component {...props} />) || <NotRole {...rest} />;
        return <Route {...rest} render={props => renderComponent(props)} />;
      }
      case ROLE.SERVICE_VENDOR: {
        if (rest.path === '/' || rest.path === '/ds/') {
          return (
            <Route {...rest} render={() => <Redirect to="/ds/staff/" />} />
          );
        }
        const renderComponent = props =>
          (auth && <Component {...props} />) || <NotRole {...rest} />;
        return <Route {...rest} render={props => renderComponent(props)} />;
      }
      case ROLE.COMPANY_ADMIN: {
        // かん助 URL判定
        if (rest.path === `/${PROVIDER.cansuke}/`) {
          const url = `/${PROVIDER.cansuke}/ds/timeline/`;
          return <Route {...rest} render={() => <Redirect to={url} />} />;
        }
        if (rest.path === '/' || rest.path === '/ds/') {
          if (isMobile && operatorId) {
            const url = `/ds/timelinetile/${operatorId}`;
            return <Route {...rest} render={() => <Redirect to={url} />} />;
          }
        }
        const renderComponent =
          // 現在は企業管理者はすべてのページ参照が可能
          props => auth && <Component {...props} />;
        return <Route {...rest} render={props => renderComponent(props)} />;
      }
      case ROLE.ROOT: {
        if (rest.path === '/' || rest.path === '/ds/') {
          return (
            <Route {...rest} render={() => <Redirect to="/ds/company/" />} />
          );
        }
        const renderComponent = props =>
          (auth && <Component {...props} />) || <NotRole {...rest} />;
        return <Route {...rest} render={props => renderComponent(props)} />;
      }
      default: {
        const renderComponent = props =>
          (auth && <Component {...props} />) || <NotRole {...rest} />;
        return <Route {...rest} render={props => renderComponent(props)} />;
      }
    }
  };
  ExRoute.propTypes = {
    component: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
      .isRequired,
    isRemote: PropTypes.bool,
  };
  ExRoute.defaultProps = {
    isRemote: false,
  };

  const {
    dashBoard,
    media,
    timeline,
    timelineTile,
    trash,
    issue,
    staff,
    company,
    device,
    master,
    remote,
    activation,
    group,
    actionLog,
    report,
    accessControl,
  } = config.customRole.pageAccess;
  return (
    <Switch>
      <ExRoute exact path="/ds/" component={MainContainer} roles={dashBoard} />
      <ExRoute
        exact
        path="/ds/activation/"
        component={ActivationContainer}
        roles={activation}
      />
      <ExRoute
        exact
        path="/ds/media/:mediaId"
        component={MediaContainer}
        roles={media}
      />
      <ExRoute
        exact
        path="/cansuke/ds/media/:mediaId"
        component={MediaContainer}
        roles={media}
      />
      <ExRoute
        exact
        path="/ds/timeline/"
        component={TimeLineContainer}
        roles={timeline}
      />
      <ExRoute
        exact
        path="/cansuke/ds/timeline/"
        component={TimeLineContainer}
        roles={timeline}
      />
      <ExRoute
        exact
        path="/ds/timelinetile/:operatorId"
        component={TimeLineTileContainer}
        roles={timelineTile}
      />
      <ExRoute
        exact
        path="/ds/timelinelist/:operatorId"
        component={TimeLineTileContainer}
        roles={timelineTile}
      />
      <ExRoute
        exact
        path={`${PAGE_PATH.DS_REMOTE}/pre/:command`}
        component={RemotePreparationContainer}
        roles={remote}
        isRemote
      />
      <ExRoute
        exact
        path={`${PAGE_PATH.DS_REMOTE}/:command`}
        component={RemoteContainer}
        roles={remote}
        isRemote
      />
      <ExRoute
        exact
        path="/ds/issue/"
        component={IssueContainer}
        roles={issue}
      />
      <ExRoute
        exact
        path="/ds/staff"
        component={StaffContainer}
        roles={staff}
      />
      <ExRoute
        exact
        path="/ds/company"
        component={CompanyContainer}
        roles={company}
      />
      <ExRoute
        exact
        path="/ds/group"
        component={GroupContainer}
        roles={group}
      />
      <ExRoute
        exact
        path="/ds/map"
        component={MapContainer}
        roles={dashBoard}
      />
      <ExRoute
        exact
        path="/ds/device"
        component={DeviceContainer}
        roles={device}
      />
      <ExRoute
        exact
        path="/ds/master"
        component={MasterContainer}
        roles={master}
      />
      <ExRoute
        exact
        path="/ds/action-log"
        component={ActionLogContainer}
        roles={actionLog}
      />
      <ExRoute
        exact
        path={PAGE_PATH.DS_ACCESS_CONTROL}
        component={AccessControlContainer}
        roles={accessControl}
      />
      <ExRoute
        exact
        path="/ds/report"
        component={ReportContainer}
        roles={report}
      />
      <ExRoute
        exact
        path={PAGE_PATH.DS_REPORT_WITH_REPORT_ID}
        component={ReportEditContainer}
        roles={report}
      />
      <ExRoute
        exact
        path="/ds/report/:reportId"
        component={ReportDetailContainer}
        roles={report}
      />
      <ExRoute exact path="/" component={MainContainer} roles={dashBoard} />
      <ExRoute
        exact
        path="/activation/"
        component={ActivationContainer}
        roles={activation}
      />
      <ExRoute
        exact
        path="/media/:mediaId"
        component={MediaContainer}
        roles={media}
      />
      <ExRoute
        exact
        path="/cansuke/media/:mediaId"
        component={MediaContainer}
        roles={media}
      />
      <ExRoute
        exact
        path="/timeline/"
        component={TimeLineContainer}
        roles={timeline}
      />
      <ExRoute
        exact
        path="/cansuke/"
        component={TimeLineContainer}
        roles={timeline}
      />
      <ExRoute
        exact
        path="/timelinetile/:operatorId"
        component={TimeLineTileContainer}
        roles={timelineTile}
      />
      <ExRoute
        exact
        path="/timelinelist/:operatorId"
        component={TimeLineTileContainer}
        roles={timelineTile}
      />
      <ExRoute exact path="/trash/" component={TrashContainer} roles={trash} />
      <ExRoute
        exact
        path="/trash/media/:mediaId"
        component={TrashMediaContainer}
        roles={trash}
      />
      <ExRoute
        exact
        path={`${PAGE_PATH.REMOTE}/pre/:command`}
        component={RemotePreparationContainer}
        roles={remote}
        isRemote
      />
      <ExRoute
        exact
        path={`${PAGE_PATH.REMOTE}/:command`}
        component={RemoteContainer}
        roles={remote}
        isRemote
      />
      <ExRoute exact path="/issue/" component={IssueContainer} roles={issue} />
      <ExRoute exact path="/staff" component={StaffContainer} roles={staff} />
      <ExRoute
        exact
        path="/company"
        component={CompanyContainer}
        roles={company}
      />
      <ExRoute exact path="/group" component={GroupContainer} roles={group} />
      <ExRoute exact path="/map" component={MapContainer} roles={dashBoard} />
      <ExRoute
        exact
        path="/device"
        component={DeviceContainer}
        roles={device}
      />
      <ExRoute
        exact
        path="/master"
        component={MasterContainer}
        roles={master}
      />
      <ExRoute
        exact
        path="/log"
        component={ActionLogContainer}
        roles={actionLog}
      />
      <Route path="/device-auth" component={DeviceAuthPage} />
      <Route exact path={PAGE_PATH.DS_LOGOUT} component={V2LogoutContainer} />
      <Route exact path="/ds/login" component={V2LoginContainer} />
      <Route
        exact
        path="/ds/loginfailure/:errorCode"
        component={LoginFailure}
      />
      <Route exact path="/logout" component={V2LogoutContainer} />
      <Route exact path="/login" component={V2LoginContainer} />
      <Route exact path="/loginfailure/:errorCode" component={LoginFailure} />
      <ExRoute component={NotFound} />
    </Switch>
  );
};

ExApp.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  apiToken: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  innerRole: PropTypes.number.isRequired,
  operatorId: PropTypes.number.isRequired,
  acquiredMaster: PropTypes.bool.isRequired,
};

const App = ({
  apiToken,
  dispatch,
  isMobile,
  locale,
  innerRole,
  operatorId,
  acquiredMaster,
}) => (
  <IntlProvider locale={locale} messages={flattenMessages(messages[locale])}>
    <BrowserRouter>
      <ExApp
        apiToken={apiToken}
        dispatch={dispatch}
        isMobile={isMobile}
        locale={locale}
        innerRole={innerRole}
        operatorId={operatorId}
        acquiredMaster={acquiredMaster}
      />
    </BrowserRouter>
  </IntlProvider>
);

App.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  apiToken: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  innerRole: PropTypes.number.isRequired,
  operatorId: PropTypes.number.isRequired,
  acquiredMaster: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  isMobile: state.main.isMobile,
  apiToken: state.main.apiToken,
  locale: state.main.locale,
  innerRole: state.main.innerRole,
  operatorId: state.main.userInfo.operatorId,
  acquiredMaster: state.main.acquiredMaster,
});

export default connect(mapStateToProps)(App);
